/*
 * Copyright 2024 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/mobile-mobile/blob/master/LICENSE.md
 */
package org.mifos.mobile.core.common

import kotlinx.datetime.DateTimePeriod
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.format
import kotlinx.datetime.format.FormatStringsInDatetimeFormats
import kotlinx.datetime.format.byUnicodePattern
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@Suppress("TooManyFunctions")
@OptIn(FormatStringsInDatetimeFormats::class)
object DateHelper {
    /*
     * This is the full month format for the date picker.
     * "dd MM yyyy" is the format of the date picker.
     */
    const val FULL_MONTH = "dd MM yyyy"

    /*
     * This is the short month format for the date picker.
     * "dd-MM-yyyy" is the format of the date picker.
     */
    const val SHORT_MONTH = "yyyy-MM-dd"

    const val MONTH_FORMAT = "dd MMMM"

    private val fullMonthFormat = LocalDateTime.Format {
        byUnicodePattern(FULL_MONTH)
    }

    private val shortMonthFormat = LocalDateTime.Format {
        byUnicodePattern(SHORT_MONTH)
    }

    /**
     * the result string uses the list given in a reverse order ([x, y, z] results in "z y x")
     *
     * @param integersOfDate [year-month-day] (ex [2016, 4, 14])
     * @return date in the format day month year (ex 14 Apr 2016)
     */
    fun getDateAsString(integersOfDate: List<Int>): String {
        val stringBuilder = StringBuilder()
        stringBuilder.append(integersOfDate[2])
            .append(' ')
            .append(getMonthName(integersOfDate[1]))
            .append(' ')
            .append(integersOfDate[0])
        return stringBuilder.toString()
    }

    @OptIn(ExperimentalTime::class)
    fun getFormattedDateWithPrefix(dateList: List<Int>): String {
        val inputDate = LocalDate(dateList[0], dateList[1], dateList[2])
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

        val formatted = "${inputDate.month.number} ${getMonthName(inputDate.month.number)} ${inputDate.year}"

        val today = now
        val yesterday = today.minus(1, DateTimeUnit.DAY)

        // Last Week
        val currentDayOfWeek = today.dayOfWeek.isoDayNumber
        val startOfThisWeek = today.minus(currentDayOfWeek - 1, DateTimeUnit.DAY)
        val startOfLastWeek = startOfThisWeek.minus(7, DateTimeUnit.DAY)
        val endOfLastWeek = startOfThisWeek.minus(1, DateTimeUnit.DAY)

        // Last Month
        val firstOfThisMonth = LocalDate(today.year, today.month.number, 1)
        val lastMonthDate = firstOfThisMonth.minus(1, DateTimeUnit.MONTH)
        val startOfLastMonth = LocalDate(lastMonthDate.year, lastMonthDate.month.number, 1)
        val endOfLastMonth = firstOfThisMonth.minus(1, DateTimeUnit.DAY)

        // Last Year
        val startOfThisYear = LocalDate(today.year, 1, 1)
        val startOfLastYear = startOfThisYear.minus(1, DateTimeUnit.YEAR)
        val endOfLastYear = startOfThisYear.minus(1, DateTimeUnit.DAY)

        return when {
            inputDate == today -> "Today, $formatted"
            inputDate == yesterday -> "Yesterday, $formatted"
            inputDate in startOfLastWeek..endOfLastWeek -> "Last Week, $formatted"
            inputDate in startOfLastMonth..endOfLastMonth -> "Last Month, $formatted"
            inputDate in startOfLastYear..endOfLastYear -> "Last Year, $formatted"
            else -> formatted
        }
    }

    fun getDateAsString(integersOfDate: List<Long>, pattern: String): String {
        return getFormatConverter(
            currentFormat = FULL_MONTH,
            requiredFormat = pattern,
            dateString = getDateAsString(integersOfDate.map { it.toInt() }),
        )
    }

    @OptIn(ExperimentalTime::class)
    fun isDarkModeBasedOnTime(
        startHour: Int,
        startMinute: Int,
        endHour: Int,
        endMinute: Int,
    ): Boolean {
        val now = Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault())
            .time

        val currentMinutes = now.hour * 60 + now.minute
        val startMinutes = startHour * 60 + startMinute
        val endMinutes = endHour * 60 + endMinute

        return if (startMinutes < endMinutes) {
            // Same-day range (e.g., 06:00 → 18:00)
            currentMinutes in startMinutes until endMinutes
        } else {
            // Cross-midnight range (e.g., 18:00 → 06:00)
            currentMinutes !in endMinutes..<startMinutes
        }
    }

    fun formatTimeRange(
        startHour: Int,
        startMinute: Int,
        endHour: Int,
        endMinute: Int,
    ): String {
        fun format(hour: Int, minute: Int): String {
            val period = if (hour < 12) "AM" else "PM"
            val hour12 = when (hour % 12) {
                0 -> 12
                else -> hour % 12
            }
            return "$hour12:${minute.toString().padStart(2, '0')} $period"
        }

        return "${format(startHour, startMinute)} - ${format(endHour, endMinute)}"
    }

    private val monthMap = mapOf(
        "Jan" to 1, "Feb" to 2, "Mar" to 3, "Apr" to 4,
        "May" to 5, "Jun" to 6, "Jul" to 7, "Aug" to 8,
        "Sep" to 9, "Oct" to 10, "Nov" to 11, "Dec" to 12,
    )

    private fun getMonthNumber(monthName: String): Int {
        return monthMap[monthName]
            ?: throw IllegalArgumentException("Invalid month name: $monthName")
    }

    fun getDateAsList(date: String): List<Int> {
        val dateList = date.split(" ")

        val day = dateList[0].toInt()
        val month = getMonthNumber(dateList[1])
        val year = dateList[2].toInt()

        return listOf(year, month, day)
    }

    @OptIn(ExperimentalTime::class)
    fun subtractTime(number: Int, unit: String): Long {
        val now: Instant = Clock.System.now()
        val daysToSubtract = when (unit.lowercase()) {
            "week", "weeks" -> now.minus((number * 7).days).toEpochMilliseconds()
            "month", "months" -> now.minus(DateTimePeriod(months = number), TimeZone.UTC).toEpochMilliseconds()
            else -> 0
        }
        return daysToSubtract
    }

    /**
     * This Method converting the dd-MM-yyyy format type date string into dd MMMM yyyy
     *
     * @param format     Final Format of date string
     * @param dateString date string
     * @return dd MMMM yyyy format date string.
     */
    fun getSpecificFormat(format: String, dateString: String): String {
        val input = LocalDate.Format { byUnicodePattern(format) }
        val localDate = input.parse(dateString)

        val day = localDate.day.toString().padStart(2, '0')
        val month = monthNumberToAbbreviation[localDate.month.number]
        val year = localDate.year

        return "$day $month $year"
    }

    private val monthNumberToAbbreviation = monthMap.entries.associate { (k, v) -> v to k }

    @OptIn(ExperimentalTime::class)
    fun getDateAsLongFromList(integersOfDate: List<Int>?): Long? {
        if (integersOfDate == null) return null
        val dateStr = getDateAsString(integersOfDate)
        return try {
            val dateList = getDateAsList(dateStr)
            val localDate = LocalDate(dateList[0], dateList[1], dateList[2])
            localDate.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
        } catch (e: Exception) {
            null
        }
    }

    private fun getFormatConverter(
        currentFormat: String,
        requiredFormat: String,
        dateString: String,
    ): String {
        val pickerFormat = LocalDateTime.Format { byUnicodePattern(currentFormat) }
        val finalFormat = LocalDateTime.Format { byUnicodePattern(requiredFormat) }

        return pickerFormat.parse(dateString).format(finalFormat)
    }

    /**
     * Gets the date string in the format "dd-MM-yyyy" from an array of integers representing the year, month, and day.
     *
     * @param dateComponents An array of three integers representing the year, month, and day, e.g. [2024, 11, 10]
     * @return The date string in the format "dd-MM-yyyy", e.g. "10-11-2024"
     */
    fun formatTransferDate(dateComponents: List<Int>, pattern: String = SHORT_MONTH): String {
        require(dateComponents.size == 3) { "dateComponents must have exactly 3 elements" }
        val (year, month, day) = dateComponents

        val localDate = LocalDate(year, Month(month), day)
        return localDate.format(pattern)
    }

    // Extension function to format LocalDate
    fun LocalDate.format(pattern: String): String {
        val year = this.year.toString().padStart(4, '0')
        val month = this.month.toString().padStart(2, '0')
        val day = this.day.toString().padStart(2, '0')

        return pattern
            .replace("yyyy", year)
            .replace("MM", month)
            .replace("dd", day)
    }

    /**
     * @param month an integer from 1 to 12
     * @return string representation of the month like Jan or Feb..etc
     */
    private fun getMonthName(month: Int): String {
        return when (month) {
            1 -> "Jan"
            2 -> "Feb"
            3 -> "Mar"
            4 -> "Apr"
            5 -> "May"
            6 -> "Jun"
            7 -> "Jul"
            8 -> "Aug"
            9 -> "Sep"
            10 -> "Oct"
            11 -> "Nov"
            12 -> "Dec"
            else -> throw IllegalArgumentException("Month should be between 1 and 12")
        }
    }

    /**
     * Input timestamp string in milliseconds
     * Example timestamp "1698278400000"
     * Output examples: "dd-MM-yyyy" - "14-04-2016"
     */
    @OptIn(ExperimentalTime::class)
    fun getDateAsStringFromLong(timeInMillis: Long): String {
        val instant = Instant.fromEpochMilliseconds(timeInMillis)
            .toLocalDateTime(TimeZone.currentSystemDefault())

        return instant.format(shortMonthFormat)
    }

    /**
     * Input timestamp string in milliseconds
     * Example timestamp "1698278400000"
     * Output examples: "14 April"
     */
    @OptIn(ExperimentalTime::class)
    fun getMonthAsStringFromLong(timeInMillis: Long): String {
        val instant = Instant.fromEpochMilliseconds(timeInMillis)
            .toLocalDateTime(TimeZone.currentSystemDefault())

        val monthName = instant.month.name.lowercase().capitalize()

        return "${instant.day} $monthName"
    }

    @OptIn(ExperimentalTime::class)
    fun getDateMonthYearString(timeInMillis: Long): String {
        val instant = Instant.fromEpochMilliseconds(timeInMillis)
            .toLocalDateTime(TimeZone.currentSystemDefault())

        val day = instant.day
        val month = instant.month.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
        val year = instant.year

        return "$day $month $year"
    }

    /**
     * Gets the date string in the format "day month year" from an array of integers representing the day and month.
     *
     * @param integersOfDate An array of two integers representing the day and month, e.g. [11, 10]
     * @return The date string in the format "day month year", e.g. "11 October"
     */
    fun getDateMonthString(integersOfDate: List<Int>): String {
        require(integersOfDate.size == 2) { "integersOfDate must have exactly 2 elements" }
        val (day, month) = integersOfDate

        val monthName = when (month) {
            1 -> "January"
            2 -> "February"
            3 -> "March"
            4 -> "April"
            5 -> "May"
            6 -> "June"
            7 -> "July"
            8 -> "August"
            9 -> "September"
            10 -> "October"
            11 -> "November"
            12 -> "December"
            else -> throw IllegalArgumentException("Invalid month value: $month")
        }

        return "$day $monthName"
    }

    /**
     * Gets the date string in the format "dd MMMM yyyy" from a list of integers representing [day, month, year].
     *
     * @param integersOfDate A list of three integers representing [day, month, year], e.g. [8, 3, 2025]
     * @return The date string in the format "08 March 2025"
     */
    fun getDateMonthYearString(integersOfDate: List<Int>): String {
        require(integersOfDate.size == 3) { "integersOfDate must have exactly 3 elements" }
        val (day, month, year) = integersOfDate

        val monthName = when (month) {
            1 -> "January"
            2 -> "February"
            3 -> "March"
            4 -> "April"
            5 -> "May"
            6 -> "June"
            7 -> "July"
            8 -> "August"
            9 -> "September"
            10 -> "October"
            11 -> "November"
            12 -> "December"
            else -> throw IllegalArgumentException("Invalid month value: $month")
        }

        val formattedDay = if (day < 10) "0$day" else "$day"

        return "$formattedDay $monthName $year"
    }

    /**
     * Handles the specific format "yyyy-MM-dd HH:mm:ss.SSSSSS"
     * For example "2024-09-19 05:41:18.558995"
     * Possible outputs depending on current date:
     * "Today at 05:41"
     * "Tomorrow at 05:41"
     */
    @OptIn(ExperimentalTime::class)
    fun String.toFormattedDateTime(): String {
        // Parse the datetime string
        val dateTime = try {
            // Split into date and time parts
            val (datePart, timePart) = this.split(" ")
            // Remove microseconds from time part
            val simplifiedTime = timePart.split(".")[0]
            // Combine date and simplified time
            val isoString = "${datePart}T$simplifiedTime"
            // Parse to LocalDateTime
            LocalDateTime.parse(isoString)
        } catch (e: Exception) {
            return this // Return original string if parsing fails
        }

        val timeZone = TimeZone.currentSystemDefault()
        val now = Clock.System.now()
        val nowDateTime = now.toLocalDateTime(timeZone)

        return when {
            // Same year
            nowDateTime.year == dateTime.year -> {
                when {
                    // Same month
                    nowDateTime.month.number == dateTime.month.number -> {
                        when {
                            // Tomorrow
                            dateTime.month.number - nowDateTime.month.number == 1 -> {
                                "Tomorrow at ${dateTime.format()}"
                            }
                            // Today
                            dateTime.month.number == nowDateTime.month.number -> {
                                "Today at ${dateTime.format()}"
                            }
                            // Yesterday
                            nowDateTime.month.number - dateTime.month.number == 1 -> {
                                "Yesterday at ${dateTime.format()}"
                            }
                            // Same month but different day
                            else -> {
                                "${
                                    dateTime.month.name.lowercase().capitalize()
                                } ${dateTime.month.number}, ${dateTime.format()}"
                            }
                        }
                    }
                    // Different month, same year
                    else -> {
                        "${
                            dateTime.month.name.lowercase().capitalize()
                        } ${dateTime.month.number}, ${dateTime.format()}"
                    }
                }
            }
            // Different year
            else -> {
                "${
                    dateTime.month.name.lowercase().capitalize()
                } ${dateTime.month.number} ${dateTime.year}, ${dateTime.format()}"
            }
        }
    }

    /**
     * Input timestamp string in milliseconds
     * Example timestamp "1698278400000"
     * Output examples:
     * "Today at 12:00"
     * "Tomorrow at 15:30"
     */
    @OptIn(ExperimentalTime::class)
    fun String.toPrettyDate(): String {
        val timestamp = this.toLong()
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val timeZone = TimeZone.currentSystemDefault()
        val nowDateTime = Clock.System.now().toLocalDateTime(timeZone)
        val neededDateTime = instant.toLocalDateTime(timeZone)

        return when {
            // Same year
            nowDateTime.year == neededDateTime.year -> {
                when {
                    // Same month
                    nowDateTime.month.number == neededDateTime.month.number -> {
                        when {
                            // Tomorrow
                            neededDateTime.month.number - nowDateTime.month.number == 1 -> {
                                val time = neededDateTime.format()
                                "Tomorrow at $time"
                            }
                            // Today
                            neededDateTime.month.number == nowDateTime.month.number -> {
                                val time = neededDateTime.format()
                                "Today at $time"
                            }
                            // Yesterday
                            nowDateTime.month.number - neededDateTime.month.number == 1 -> {
                                val time = neededDateTime.format()
                                "Yesterday at $time"
                            }
                            // Same month but different day
                            else -> {
                                "${
                                    neededDateTime.month.name.lowercase().capitalize()
                                } ${neededDateTime.month.number}, ${neededDateTime.format()}"
                            }
                        }
                    }
                    // Different month, same year
                    else -> {
                        "${
                            neededDateTime.month.name.lowercase().capitalize()
                        } ${neededDateTime.month.number}, ${neededDateTime.format()}"
                    }
                }
            }
            // Different year
            else -> {
                "${
                    neededDateTime.month.name.lowercase().capitalize()
                } ${neededDateTime.month.number} ${neededDateTime.year}, ${neededDateTime.format()}"
            }
        }
    }

    // Helper function to format time
    private fun LocalDateTime.format(): String {
        return "${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}"
    }

    // Extension to capitalize first letter
    private fun String.capitalize() = replaceFirstChar {
        if (it.isLowerCase()) it.titlecase() else it.toString()
    }

    @OptIn(ExperimentalTime::class)
    val currentDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())

    /**
     * This is the full date format for the date picker.
     * "dd MM yyyy" is the format of the date picker.
     */
    val formattedFullDate = currentDate.format(fullMonthFormat)

    /**
     * This is the short date format for the date picker.
     * "dd-MM-yyyy" is the format of the date picker.
     */
    val formattedShortDate = currentDate.format(shortMonthFormat)

    fun getMonth(month: Int): String {
        require(month in 1..12) { "Month should be between 1 and 12" }
        return Month.entries[month - 1].name.lowercase().replaceFirstChar { it.uppercase() }
    }
}
